// src/components/admin/teamTaskbox/widgets/MyTasksWidget.js
import React from 'react';
import { CheckSquare, Calendar, Clock, Flag } from 'lucide-react';
import { useAuth } from '../../../../hooks/useAuth';
import teamTasksService from '../../../../services/teamTasks.service';

const MyTasksWidget = ({ tasks, onTaskClick }) => {
    const { user } = useAuth();

    // Filter tasks assigned to current user
    const myTasks = tasks.filter(task => {
        if (!task.assigned_employee_ids || !Array.isArray(task.assigned_employee_ids)) return false;
        return task.assigned_employee_ids.includes(user?.id);
    }).slice(0, 5); // Show top 5

    const priorityColors = {
        immediate: 'text-red-600 dark:text-red-400',
        urgent: 'text-orange-600 dark:text-orange-400',
        next_in_line: 'text-yellow-600 dark:text-yellow-400',
        scheduled: 'text-green-600 dark:text-green-400',
        deferred: 'text-gray-600 dark:text-gray-400'
    };

    const statusColors = {
        pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        under_review: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        on_hold: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No deadline';
        // Extract YYYY-MM-DD from ISO string
        const dateOnly = dateString.split('T')[0];
        const [year, month, day] = dateOnly.split('-');
        // Create date in local timezone without UTC conversion
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-purple-600" />
                    My Tasks
                </h3>
                <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-sm font-medium">
                    {myTasks.length} active
                </span>
            </div>

            {myTasks.length === 0 ? (
                <div className="text-center py-8">
                    <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">No tasks assigned to you</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {myTasks.map(task => {
                        const progress = teamTasksService.calculateProgress(task);

                        return (
                            <div
                                key={task.id}
                                onClick={() => onTaskClick(task)}
                                className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-start gap-2 flex-1">
                                        <Flag className={`w-4 h-4 ${priorityColors[task.priority]} flex-shrink-0 mt-0.5`} fill="currentColor" />
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-gray-900 dark:text-white line-clamp-1 mb-1">
                                                {task.title}
                                            </h4>
                                        </div>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[task.status]} whitespace-nowrap ml-2`}>
                                        {task.status.replace('_', ' ')}
                                    </span>
                                </div>

                                {/* Progress Bar */}
                                <div className="mb-3">
                                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                                        <span>Progress</span>
                                        <span>{progress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                                        <div
                                            className={`h-1.5 rounded-full transition-all ${
                                                progress === 100 ? 'bg-green-600' : 'bg-blue-600'
                                            }`}
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                                    {task.deadline && (
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            <span>{formatDate(task.deadline)}</span>
                                        </div>
                                    )}
                                    {task.estimated_hours && (
                                        <div className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            <span>{task.estimated_hours}h</span>
                                        </div>
                                    )}
                                    {task.overdue_days > 0 && (
                                        <span className="text-red-600 dark:text-red-400 font-medium">
                                            {task.overdue_days}d overdue
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyTasksWidget;
